import api from './api';

// ========================================
// LEGACY SUPPORT API (for existing chat/request functionality)
// ========================================

const getBaseURL = () => {
  if (process.env.REACT_APP_USE_PROXY === 'true') {
    return '/api';
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  const apiEnv = process.env.REACT_APP_API_ENV;
  if (apiEnv === 'production' || apiEnv === 'prod') {
    return 'https://www.bonyad-hub.com/api';
  }
  if (apiEnv === 'staging' || apiEnv === 'eb') {
    return 'https://aac2o4ebrc.execute-api.me-central-1.amazonaws.com/api';
  }
  return 'https://www.bonyad-hub.com/api';
};

// Legacy supportApi for chat and request management
export const supportApi = {
  getBaseUrl: () => getBaseURL(),

  getRequestById: async (token: string, requestId: number) => {
    const response = await api.get(`/support/requests/${requestId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getAllRequests: async (token: string, filter?: string) => {
    const params = filter ? { filter } : {};
    const response = await api.get('/support/requests', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getPendingRequests: async (token: string) => {
    const response = await api.get('/support/requests', {
      params: { filter: 'pending' },
      headers: { Authorization: `Bearer ${token}` },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  getActiveRequests: async (token: string) => {
    const response = await api.get('/support/requests', {
      params: { filter: 'active' },
      headers: { Authorization: `Bearer ${token}` },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  getMyAssigned: async (token: string) => {
    const response = await api.get('/support/my-assigned', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  getMyChatRooms: async (token: string) => {
    const response = await api.get('/support/chat/rooms/my', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  assignRequest: async (token: string, requestId: number, adminId: number) => {
    const response = await api.put(`/support/requests/${requestId}/assign`,
      { adminId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  acceptRequest: async (token: string, requestId: number) => {
    const response = await api.post(`/support/requests/${requestId}/accept`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  sendChatMessage: async (token: string, receiverId: number, content: string, projectId?: number) => {
    const response = await api.post('/chat/send',
      { receiverId, content, projectId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  sendChatMessageWithFile: async (token: string, formData: FormData) => {
    // Requires receiverId, optional content, optional file, optional requestId
    const response = await api.post('/support/send-with-file', formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.message || response.data;
  },

  resolveRequest: async (token: string, requestId: number) => {
    const response = await api.put(`/support/requests/${requestId}/resolve`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },
};

// ========================================
// AI CHATBOT CONVERSATIONS API
// ========================================

export interface AIChatService {
  id: number;
  nameEn: string;
  nameAr: string;
}

export interface AIChatRegion {
  id: number;
  nameEn: string;
  nameAr: string;
}

export interface AIChatUser {
  id: number;
  name: string;
  phoneNumber: string;
  email?: string;
}

export interface AIChatConversation {
  id: number;
  conversationId: string;
  userMessage: string;
  aiResponse: string;
  detectedServices?: AIChatService[];
  detectedRegions?: AIChatRegion[];
  language?: string;
  intent?: string;
  convertedToLiveAgent: boolean;
  supportRequestId?: number | null;
  createdAt: string;
  user?: AIChatUser | null;
}

export interface AIChatStatistics {
  totalConversations: number;
  totalUsers: number;
  conversionRate: number;
  convertedCount: number;
  averageConversationsPerUser: number;
  mostCommonIntent?: string;
  languageDistribution?: {
    en?: number;
    ar?: number;
    other?: number;
    [key: string]: number | undefined;
  };
  topServices?: Array<{
    serviceId: number;
    serviceNameEn: string;
    serviceNameAr: string;
    count: number;
  }>;
  topRegions?: Array<{
    regionId: number;
    regionNameEn: string;
    regionNameAr: string;
    count: number;
  }>;
}

export interface AIChatConversationsResponse {
  content: AIChatConversation[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export const aiChatApi = {
  // Get all conversations with optional filters
  // Query parameters: page, size, userId, conversationId, language, converted
  getConversations: async (
    token: string,
    params?: {
      page?: number;
      size?: number;
      userId?: number;
      conversationId?: string;
      language?: 'en' | 'ar';
      converted?: boolean;
    }
  ): Promise<AIChatConversationsResponse> => {
    const response = await api.get('/admin/ai-chat/conversations', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = response.data;

    // Ensure response matches expected format
    if (data.content) {
      return {
        content: data.content,
        totalElements: data.totalElements || 0,
        totalPages: data.totalPages || 0,
        currentPage: data.currentPage || params?.page || 0,
        pageSize: data.pageSize || data.size || params?.size || 20,
        hasNext: data.hasNext !== undefined ? data.hasNext : (data.currentPage || 0) < (data.totalPages || 0) - 1,
        hasPrevious: data.hasPrevious !== undefined ? data.hasPrevious : (data.currentPage || 0) > 0,
      };
    }

    // Fallback for non-paginated responses
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: params?.page || 0,
      pageSize: params?.size || 20,
      hasNext: false,
      hasPrevious: false,
    };
  },

  // Get conversations by user (using filter parameter)
  getConversationsByUser: async (
    token: string,
    userId: number,
    page: number = 0,
    size: number = 20
  ): Promise<AIChatConversationsResponse> => {
    return aiChatApi.getConversations(token, { userId, page, size });
  },

  // Get conversations by conversation ID (using filter parameter)
  getConversationsByConversationId: async (
    token: string,
    conversationId: string,
    page: number = 0,
    size: number = 20
  ): Promise<AIChatConversationsResponse> => {
    return aiChatApi.getConversations(token, { conversationId, page, size });
  },

  // Get conversations by support request (using filter parameter)
  getConversationsBySupportRequest: async (
    token: string,
    requestId: number,
    page: number = 0,
    size: number = 20
  ): Promise<AIChatConversationsResponse> => {
    // Note: This endpoint might need to be implemented on backend
    // For now, we'll filter by supportRequestId in the frontend
    const allConversations = await aiChatApi.getConversations(token, { page, size: 1000 });
    const filtered = allConversations.content.filter(
      conv => conv.supportRequestId === requestId
    );
    return {
      ...allConversations,
      content: filtered,
      totalElements: filtered.length,
      totalPages: Math.ceil(filtered.length / size),
    };
  },

  // Get statistics
  getStatistics: async (token: string): Promise<AIChatStatistics> => {
    const response = await api.get('/admin/ai-chat/statistics', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// ========================================
// TYPES
// ========================================

export interface SupportPriority {
  id: number;
  name: string;
  displayName: string;
  displayNameAr: string;
  colorCode: string;
  responseTimeHours: number;
  resolutionTimeHours: number;
  displayOrder: number;
  isActive: boolean;
}

export interface CreatePriorityRequest {
  name: string;
  displayName: string;
  displayNameAr: string;
  colorCode: string;
  responseTimeHours: number;
  resolutionTimeHours: number;
  displayOrder: number;
  isActive: boolean;
}

export interface UpdatePriorityRequest {
  displayName?: string;
  displayNameAr?: string;
  colorCode?: string;
  responseTimeHours?: number;
  resolutionTimeHours?: number;
  displayOrder?: number;
  isActive?: boolean;
}

export interface SubscriptionCategoryWithPriority {
  id: number;
  nameAr: string;
  nameEn: string;
  price: number;
  descriptionAr?: string;
  descriptionEn?: string;
  durationDays: number;
  isActive: boolean;
  bidsPerWeek?: number;
  supportPriority?: SupportPriority;
  finalPrice: number;
  hasActiveDiscount: boolean;
}

export interface SetCategoryPriorityRequest {
  priorityId: number;
}

export interface PriorityMappingSummary {
  byPriority: Record<string, Array<{
    id: number;
    nameEn: string;
    nameAr: string;
    price: number;
  }>>;
  noPriority: Array<{
    id: number;
    nameEn: string;
    nameAr: string;
    price: number;
  }>;
  totalCategories: number;
  categoriesWithPriority: number;
  categoriesWithoutPriority: number;
}

export interface DetailedPriorityMapping extends PriorityMappingSummary {
  byPriority: Record<string, Array<{
    id: number;
    nameEn: string;
    nameAr: string;
    price: number;
    priority: SupportPriority;
  }>>;
  availablePriorities: SupportPriority[];
}

export interface SupportActionRequest {
  id: number;
  ticketId: number;
  targetUserId: number;
  targetUser?: {
    id: number;
    name: string;
    phoneNumber: string;
  };
  actionType: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'COMPLETED' | 'FAILED';
  reason: string;
  additionalData?: Record<string, any>;
  requestedBy?: {
    id: number;
    name: string;
  };
  approvedBy?: number;
  approvedAt?: string;
  rejectedBy?: number;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
}

export interface CreateActionRequest {
  ticketId: number;
  targetUserId: number;
  actionType: string;
  reason: string;
  additionalData?: Record<string, any>;
}

export interface ApproveActionRequest {
  comments?: string;
}

export interface RejectActionRequest {
  reason: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
}

export interface SupportSurvey {
  id: number;
  ticket: {
    id: number;
    subject: string;
    status: string;
  };
  user: {
    id: number;
    name: string;
    phoneNumber: string;
    email: string;
  };
  rating: number;
  comment?: string;
  isLowRating: boolean;
  isLowRatingAlertSent: boolean;
  lowRatingAlertSentAt?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSurveyRequest {
  rating: number;
  comment?: string;
}

export interface SurveyStatistics {
  totalSurveys: number;
  averageRating: number;
  rating1Count: number;
  rating2Count: number;
  rating3Count: number;
  rating4Count: number;
  rating5Count: number;
  lowRatingCount: number;
}

// ========================================
// PRIORITY MANAGEMENT APIs (Complete SLA & Priority Management)
// ========================================

export interface PriorityWithSLA {
  id: number;
  name: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL';
  displayName: string;
  displayNameAr: string;
  colorCode: string;
  responseTimeHours: number;
  resolutionTimeHours: number;
  responseTimeFormatted: string;
  resolutionTimeFormatted: string;
  displayOrder: number;
  isActive: boolean;
}

export interface UpdatePrioritySLARequest {
  displayName?: string;
  displayNameAr?: string;
  colorCode?: string;
  responseTimeHours?: number;
  resolutionTimeHours?: number;
  displayOrder?: number;
  isActive?: boolean;
}

export interface UpdatePrioritySLAOnlyRequest {
  responseTimeHours: number;
  resolutionTimeHours: number;
}

export interface CreatePriorityRequest {
  name: string;
  displayName: string;
  displayNameAr: string;
  colorCode: string;
  responseTimeHours: number;
  resolutionTimeHours: number;
  displayOrder: number;
  isActive: boolean;
}

export const priorityApi = {
  // Get all priorities with SLA times
  getAll: async (): Promise<PriorityWithSLA[]> => {
    const response = await api.get('/admin/support/priorities');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get single priority by ID
  getById: async (id: number): Promise<PriorityWithSLA> => {
    const response = await api.get(`/admin/support/priorities/${id}`);
    return response.data;
  },

  // Update priority SLA times
  update: async (id: number, data: UpdatePrioritySLARequest): Promise<PriorityWithSLA> => {
    const response = await api.put(`/admin/support/priorities/${id}`, data);
    return response.data;
  },

  // Update priority SLA times only (convenience)
  updateSLA: async (id: number, data: UpdatePrioritySLAOnlyRequest): Promise<PriorityWithSLA> => {
    const response = await api.put(`/admin/support/priorities/${id}/sla`, data);
    return response.data;
  },

  // Create new priority
  create: async (data: CreatePriorityRequest): Promise<PriorityWithSLA> => {
    const response = await api.post('/admin/support/priorities', data);
    return response.data;
  },

  // Delete priority
  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/support/priorities/${id}`);
  },
};

// ========================================
// CATEGORY PRIORITY MAPPING APIs
// ========================================

export const categoryPriorityApi = {
  // Get all categories with priority
  getCategories: async (): Promise<SubscriptionCategoryWithPriority[]> => {
    const response = await api.get('/admin/subscriptions/categories');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get category by ID with priority
  getCategoryById: async (id: number): Promise<SubscriptionCategoryWithPriority> => {
    const response = await api.get(`/admin/subscriptions/categories/${id}`);
    return response.data;
  },

  // Set priority for category
  setPriority: async (id: number, data: SetCategoryPriorityRequest): Promise<SubscriptionCategoryWithPriority> => {
    const response = await api.put(`/admin/subscriptions/categories/${id}/priority`, data);
    return response.data;
  },

  // Remove priority from category
  removePriority: async (id: number): Promise<SubscriptionCategoryWithPriority> => {
    const response = await api.delete(`/admin/subscriptions/categories/${id}/priority`);
    return response.data;
  },

  // Get priority mapping summary
  getMappingSummary: async (): Promise<PriorityMappingSummary> => {
    const response = await api.get('/admin/subscriptions/categories/priority-mapping');
    return response.data;
  },
};

// ========================================
// SUPPORT ACTION REQUESTS APIs
// ========================================

export interface TicketListItem {
  id: number;
  subject: string;
  status: string;
  userId: number;
  userName: string;
  userPhoneNumber: string;
  createdAt: string;
}

export interface UserListItem {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  role: string;
}

export interface ActionRequestStatistics {
  totalRequests: number;
  pendingRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
}

export const supportActionApi = {
  // Get all action requests
  getAll: async (params?: {
    status?: string;
    actionType?: string;
    ticketId?: number;
    targetUserId?: number;
    requestedBy?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  }): Promise<PaginatedResponse<SupportActionRequest>> => {
    const response = await api.get('/admin/support/actions', { params });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: params?.page || 0,
      size: params?.size || 20,
    };
  },

  // Get action request by ID
  getById: async (id: number): Promise<SupportActionRequest> => {
    const response = await api.get(`/admin/support/actions/${id}`);
    return response.data;
  },

  // Get action requests by ticket ID
  getByTicketId: async (ticketId: number): Promise<SupportActionRequest[]> => {
    const response = await api.get(`/admin/support/actions/ticket/${ticketId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get statistics
  getStatistics: async (): Promise<ActionRequestStatistics> => {
    const response = await api.get('/admin/support/actions/statistics');
    return response.data;
  },

  // Get tickets list for dropdown
  getTicketsList: async (): Promise<TicketListItem[]> => {
    const response = await api.get('/admin/support/actions/tickets/list');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get users list for dropdown
  getUsersList: async (): Promise<UserListItem[]> => {
    const response = await api.get('/admin/support/actions/users/list');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Request action (Support)
  request: async (data: CreateActionRequest): Promise<SupportActionRequest> => {
    const response = await api.post('/admin/support/actions/request', data);
    return response.data;
  },
};

// ========================================
// SUPPORT SURVEY APIs (DEPRECATED - Use unifiedReviewApi instead)
// ========================================
// @deprecated Use unifiedReviewApi.getAll() with reviewType='TICKET_SURVEY' instead

export const supportSurveyApi = {
  // Create survey (User)
  create: async (ticketId: number, data: CreateSurveyRequest): Promise<SupportSurvey> => {
    const response = await api.post(`/support/surveys/tickets/${ticketId}`, data);
    return response.data;
  },

  // Get all surveys
  getAll: async (page = 0, size = 20): Promise<PaginatedResponse<SupportSurvey>> => {
    const response = await api.get('/support/surveys', { params: { page, size } });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  // Get survey by ticket ID
  getByTicketId: async (ticketId: number): Promise<SupportSurvey> => {
    const response = await api.get(`/support/surveys/tickets/${ticketId}`);
    return response.data;
  },

  // Get low rating surveys
  getLowRatings: async (): Promise<SupportSurvey[]> => {
    const response = await api.get('/support/surveys/low-ratings');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get unsent low rating alerts
  getUnsentAlerts: async (): Promise<SupportSurvey[]> => {
    const response = await api.get('/support/surveys/low-ratings/unsent-alerts');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get survey statistics
  getStatistics: async (): Promise<SurveyStatistics> => {
    const response = await api.get('/support/surveys/statistics');
    return response.data;
  },

  // Export surveys to Excel
  exportExcel: async (lowRatingOnly = false): Promise<Blob> => {
    const response = await api.get('/support/surveys/export/excel', {
      params: { lowRatingOnly },
      responseType: 'blob',
    });
    return response.data;
  },
};

// ========================================
// CSAT SURVEY APIs (DEPRECATED - Use unifiedReviewApi instead)
// ========================================
// @deprecated Use unifiedReviewApi.getAll() with reviewType='CHAT_SURVEY' instead

export interface CSATSurvey {
  id: number;
  ticketId: number;
  customerId: number;
  customerName: string;
  agentId: number;
  agentName: string;
  rating: number;
  comment?: string;
  isFlagged: boolean;
  reviewedAt?: string | null;
  createdAt: string;
}

export interface CSATStatistics {
  totalSurveys: number;
  averageRating: number;
  flaggedCount: number;
  unreviewedFlaggedCount: number;
  responseRate: number;
}

export interface ReviewSurveyRequest {
  notes: string;
}

export const csatSurveyApi = {
  // Get all CSAT surveys
  getAll: async (): Promise<CSATSurvey[]> => {
    const response = await api.get('/admin/csat/surveys');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get flagged surveys
  getFlagged: async (): Promise<CSATSurvey[]> => {
    const response = await api.get('/admin/csat/surveys/flagged');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get unreviewed flagged surveys
  getUnreviewedFlagged: async (): Promise<CSATSurvey[]> => {
    const response = await api.get('/admin/csat/surveys/flagged/unreviewed');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get surveys by agent
  getByAgent: async (agentId: number): Promise<CSATSurvey[]> => {
    const response = await api.get(`/admin/csat/surveys/agent/${agentId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // Review flagged survey
  review: async (surveyId: number, data: ReviewSurveyRequest): Promise<CSATSurvey> => {
    const response = await api.post(`/admin/csat/surveys/${surveyId}/review`, data);
    return response.data;
  },

  // Get CSAT statistics
  getStatistics: async (): Promise<CSATStatistics> => {
    const response = await api.get('/admin/csat/statistics');
    return response.data;
  },
};

// ========================================
// UNIFIED REVIEWS & SURVEYS API
// ========================================

export type ReviewType = 'USER_REVIEW' | 'PROJECT_REVIEW' | 'SMALL_TASK_REVIEW' | 'TICKET_SURVEY' | 'CHAT_SURVEY';

export interface CategoryRating {
  categoryId: number;
  categoryNameAr: string;
  categoryNameEn: string;
  ratingValue: number;
}

export interface UnifiedReview {
  id: number;
  reviewType: ReviewType;
  reviewTypeDisplay: string;
  rating: number;
  comment?: string;
  reviewerId: number;
  reviewerName: string;
  reviewerPhone?: string;
  // Fields for USER_REVIEW / PROJECT_REVIEW
  reviewedUserId?: number;
  reviewedUserName?: string;
  projectId?: number;
  // Fields for TICKET_SURVEY
  supportTicketId?: number;
  ticketSubject?: string;
  supportAgentId?: number;
  supportAgentName?: string;
  // Fields for CHAT_SURVEY
  supportChatRequestId?: number;
  chatSubject?: string;
  // New fields from latest guide
  smallTaskRequestId?: number;
  isLowRating: boolean;
  lowRatingAlertSent?: boolean;
  lowRatingAlertSentAt?: string;
  createdAt: string;
  categoryRatings?: CategoryRating[];
}

export interface UnifiedReviewStatistics {
  totalReviews: number;
  totalLowRatings: number;
  overallAverageRating: number;
  byType: {
    [key in ReviewType]?: {
      total: number;
      lowRatings: number;
      averageRating: number;
    };
  };
}

export const unifiedReviewApi = {
  // Get all reviews with optional filters
  getAll: async (
    page = 0,
    size = 20,
    lowRatingOnly = false,
    reviewType?: ReviewType
  ): Promise<PaginatedResponse<UnifiedReview>> => {
    const params: any = { page, size, lowRatingOnly };
    if (reviewType) {
      params.reviewType = reviewType;
    }
    const response = await api.get('/admin/reviews', { params });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  // Get low rating reviews only
  getLowRatings: async (
    page = 0,
    size = 20,
    reviewType?: ReviewType
  ): Promise<PaginatedResponse<UnifiedReview>> => {
    return unifiedReviewApi.getAll(page, size, true, reviewType);
  },

  // Get review statistics
  getStatistics: async (): Promise<UnifiedReviewStatistics> => {
    const response = await api.get('/admin/reviews/statistics');
    return response.data;
  },

  // Export reviews to Excel
  exportExcel: async (lowRatingOnly = false, reviewType?: ReviewType): Promise<Blob> => {
    const params: any = { lowRatingOnly };
    if (reviewType) {
      params.reviewType = reviewType;
    }
    const response = await api.get('/admin/reviews/export/excel', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// ========================================
// SUPPORT VIEW APIs (for Customer Support Platform)
// ========================================

export const supportViewApi = {
  // Get all priorities (view only)
  getPriorities: async (): Promise<SupportPriority[]> => {
    const response = await api.get('/support/subscriptions/priorities');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get priority by ID (view only)
  getPriorityById: async (id: number): Promise<SupportPriority> => {
    const response = await api.get(`/support/subscriptions/priorities/${id}`);
    return response.data;
  },

  // Get all categories with priority (view only)
  getCategories: async (): Promise<SubscriptionCategoryWithPriority[]> => {
    const response = await api.get('/support/subscriptions/categories');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get category by ID with priority (view only)
  getCategoryById: async (id: number): Promise<SubscriptionCategoryWithPriority> => {
    const response = await api.get(`/support/subscriptions/categories/${id}`);
    return response.data;
  },

  // Get detailed priority mapping
  getDetailedMapping: async (): Promise<DetailedPriorityMapping> => {
    const response = await api.get('/support/subscriptions/categories/priority-mapping');
    return response.data;
  },
};

// ========================================
// ADMIN TICKET MANAGEMENT APIs
// ========================================

export interface AdminTicket {
  id: number;
  subject: string;
  description: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | 'WAITING_FOR_CUSTOMER' | 'ESCALATED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT' | 'CRITICAL';
  category?: string;

  // ── Flat user fields (actual API response shape) ──
  userId?: number;
  userName?: string;
  userPhoneNumber?: string;

  // ── Nested user (legacy / alternate shape) ──
  user?: {
    id: number;
    name: string;
    phoneNumber: string;
    email?: string;
  };

  // ── Assignment ──
  assignedAdminId?: number;
  assignedAdminName?: string;
  assignedAdmin?: {
    id: number;
    name: string;
  };

  // ── Category / request type ──
  requestType?: {
    id: number;
    nameEn: string;
    nameAr: string;
    parent?: { id: number; nameEn: string; nameAr: string };
  };

  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  closedById?: number | null;
  closedByName?: string | null;
  unreadMessageCount?: number;
  slaResponseDeadline?: string;
  slaResolutionDeadline?: string;
  messages?: AdminTicketMessage[];
}

export interface AdminTicketMessage {
  id: number;
  message: string;

  // ── Flat sender fields (actual API response shape) ──
  senderId?: number;
  senderName?: string;
  senderRole?: 'USER' | 'TECHNICIAN' | 'ADMIN';
  isAdminMessage?: boolean;
  isInternal?: boolean;
  isRead?: boolean;
  readAt?: string | null;

  // ── Nested sender (legacy / alternate shape) ──
  sender?: {
    id: number;
    name: string;
  };

  attachments?: string[];
  createdAt: string;

  /** @deprecated use isInternal */
  internal?: boolean;
}

export interface TicketFilters {
  status?: string;
  priority?: string;
  category?: string;
  assignedAdminId?: number;
}

export interface UpdateTicketRequest {
  status?: string;
  priority?: string;
  assignedAdminId?: number | null;
}

export interface AddTicketMessageRequest {
  message: string;
  isInternal?: boolean;
  /** @deprecated use isInternal */
  internal?: boolean;
  attachmentUrls?: string[];
}

export interface CreateManualTicketRequest {
  userId?: number;
  phoneNumber?: string;
  userName?: string;
  subject: string;
  description: string;
  categoryId?: number;
  subcategoryId?: number;
  priority?: string;
  attachmentUrls?: string[];
}

export interface TicketStatistics {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  byPriority: {
    CRITICAL?: number;
    URGENT?: number;
    HIGH?: number;
    MEDIUM?: number;
    LOW?: number;
  };
}

export const adminTicketApi = {
  // Get all tickets with optional filters
  getAll: async (filters?: TicketFilters): Promise<AdminTicket[]> => {
    const response = await api.get('/admin/support/tickets', { params: filters });
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get ticket by ID
  getById: async (id: number): Promise<AdminTicket> => {
    const response = await api.get(`/admin/support/tickets/${id}`);
    return response.data;
  },

  // Add admin message to ticket
  addMessage: async (id: number, data: AddTicketMessageRequest): Promise<AdminTicketMessage> => {
    const response = await api.post(`/admin/support/tickets/${id}/messages`, data);
    return response.data;
  },

  // Update ticket status/priority/assignment
  update: async (id: number, data: UpdateTicketRequest): Promise<AdminTicket> => {
    const response = await api.put(`/admin/support/tickets/${id}`, data);
    return response.data;
  },

  // Close ticket
  close: async (id: number): Promise<AdminTicket> => {
    const response = await api.put(`/admin/support/tickets/${id}/close`);
    return response.data;
  },

  // Get ticket statistics
  getStats: async (): Promise<TicketStatistics> => {
    const response = await api.get('/admin/support/stats');
    return response.data;
  },

  // Export single ticket as PDF
  exportPdf: async (id: number): Promise<Blob> => {
    const response = await api.get(`/admin/support/tickets/${id}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export multiple tickets as PDF
  exportPdfMultiple: async (ids: number[]): Promise<Blob> => {
    const response = await api.get('/admin/support/tickets/export/pdf', {
      params: { ids: ids.join(',') },
      responseType: 'blob',
    });
    return response.data;
  },

  // Export single ticket as Excel
  exportExcel: async (id: number): Promise<Blob> => {
    const response = await api.get(`/admin/support/tickets/${id}/export/excel`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Export multiple tickets as Excel
  exportExcelMultiple: async (ids: number[]): Promise<Blob> => {
    const response = await api.get('/admin/support/tickets/export/excel', {
      params: { ids: ids.join(',') },
      responseType: 'blob',
    });
    return response.data;
  },

  // Create ticket manually
  createManual: async (data: CreateManualTicketRequest): Promise<AdminTicket> => {
    const response = await api.post('/admin/support/tickets/manual', data);
    return response.data;
  },
};

// ========================================
// CATEGORY MANAGEMENT APIs
// ========================================

export interface SupportCategory {
  id: number;
  nameAr: string;
  nameEn: string;
  displayOrder: number;
  defaultPriority: string;
  defaultCriticality?: string;
  slaResponseHours?: number;
  slaResolutionHours?: number;
  slaResponseFormatted?: string;
  slaResolutionFormatted?: string;
  slaOverrideEnabled?: boolean;
  parentId?: number;
  parent?: SupportCategory;
  children?: SupportCategory[];
  description?: string;
  icon?: string;
}

export interface CreateCategoryRequest {
  nameAr: string;
  nameEn: string;
  defaultPriority: string;
  defaultCriticality: string;
  parentId?: number | null;
  displayOrder?: number;
  description?: string;
  icon?: string;
  slaOverrideEnabled?: boolean;
  slaResponseHoursOverride?: number;
  slaResolutionHoursOverride?: number;
}

export interface UpdateCategoryRequest extends Partial<CreateCategoryRequest> { }

export interface CategorySLA {
  categoryId: number;
  categoryName: string;
  slaOverrideEnabled: boolean;
  slaResponseHoursOverride?: number;
  slaResolutionHoursOverride?: number;
  effectiveResponseHours: number;
  effectiveResolutionHours: number;
  slaResponseFormatted: string;
  slaResolutionFormatted: string;
  defaultPriority: string;
}

export interface ReorderCategoriesRequest {
  [categoryId: string]: number;
}

export const categoryManagementApi = {
  // Get all categories
  getAll: async (): Promise<SupportCategory[]> => {
    const response = await api.get('/admin/support/categories');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get category by ID
  getById: async (id: number): Promise<SupportCategory> => {
    const response = await api.get(`/admin/support/categories/${id}`);
    return response.data;
  },

  // Get category hierarchy
  getHierarchy: async (): Promise<SupportCategory[]> => {
    const response = await api.get('/admin/support/categories/hierarchy');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get subcategories
  getSubcategories: async (id: number): Promise<SupportCategory[]> => {
    const response = await api.get(`/admin/support/categories/${id}/subcategories`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // Create category
  create: async (data: CreateCategoryRequest): Promise<SupportCategory> => {
    const response = await api.post('/admin/support/categories', data);
    return response.data;
  },

  // Update category
  update: async (id: number, data: UpdateCategoryRequest): Promise<SupportCategory> => {
    const response = await api.put(`/admin/support/categories/${id}`, data);
    return response.data;
  },

  // Delete category
  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/support/categories/${id}`);
  },

  // Reorder categories
  reorder: async (data: ReorderCategoriesRequest): Promise<void> => {
    await api.put('/admin/support/categories/reorder', data);
  },

  // Get category SLA settings
  getSLA: async (id: number): Promise<CategorySLA> => {
    const response = await api.get(`/admin/support/categories/${id}/sla`);
    return response.data;
  },

  // Update category SLA settings
  updateSLA: async (id: number, data: Partial<CategorySLA>): Promise<CategorySLA> => {
    const response = await api.put(`/admin/support/categories/${id}/sla`, data);
    return response.data;
  },

  // Get categories with SLA summary
  getSLASummary: async (): Promise<SupportCategory[]> => {
    const response = await api.get('/admin/support/categories/sla-summary');
    return Array.isArray(response.data) ? response.data : [];
  },
};

// ========================================
// CATEGORY & SUBCATEGORY SLA APIs (Public)
// ========================================

export interface CategoryWithSLA {
  id: number;
  nameAr: string;
  nameEn: string;
  description?: string;
  icon?: string;
  parentId: number | null;
  parentName: string | null;
  displayOrder: number;
  defaultPriority: string;
  defaultCriticality?: string;
  isActive: boolean;
  hasChildren: boolean;
  slaOverrideEnabled: boolean;
  slaResponseHoursOverride?: number;
  slaResolutionHoursOverride?: number;
  slaResponseHours: number;
  slaResolutionHours: number;
  slaResponseFormatted: string;
  slaResolutionFormatted: string;
  children?: CategoryWithSLA[];
}

export const categorySlaApi = {
  // Get all categories with SLA information (Public API)
  getAll: async (): Promise<CategoryWithSLA[]> => {
    const response = await api.get('/support/categories');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get root categories only (Public API)
  getRoot: async (): Promise<CategoryWithSLA[]> => {
    const response = await api.get('/support/categories/root');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get category hierarchy (Public API)
  getHierarchy: async (): Promise<CategoryWithSLA[]> => {
    const response = await api.get('/support/categories/hierarchy');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get subcategories by parent ID with SLA (Public API)
  getSubcategories: async (parentId: number): Promise<CategoryWithSLA[]> => {
    const response = await api.get(`/support/categories/${parentId}/subcategories`);
    return Array.isArray(response.data) ? response.data : [];
  },
};

// ========================================
// SLA BREACH MONITORING APIs
// ========================================

export interface SLABreach {
  id: number;
  ticketId: number;
  breachType: 'RESPONSE' | 'RESOLUTION';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  expectedDeadline: string;
  actualTime: string;
  breachDurationHours: number;
  acknowledged: boolean;
  acknowledgedAt?: string;
  acknowledgedBy?: number;
  resolutionNotes?: string;
  createdAt: string;
}

export interface SLABreachFilters {
  page?: number;
  size?: number;
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface SLABreachAnalytics {
  totalBreaches: number;
  unacknowledgedBreaches: number;
  breachesBySeverity: {
    LOW?: number;
    MEDIUM?: number;
    HIGH?: number;
    CRITICAL?: number;
  };
  breachesByType: {
    RESPONSE?: number;
    RESOLUTION?: number;
  };
  slaComplianceRate: number;
  periodStart: string;
  periodEnd: string;
}

export interface AcknowledgeBreachRequest {
  resolutionNotes: string;
}

export const slaBreachApi = {
  // Get all SLA breaches
  getAll: async (filters?: SLABreachFilters): Promise<{ content: SLABreach[]; totalElements: number; totalPages: number; currentPage: number; size: number }> => {
    const response = await api.get('/admin/support/sla-breaches', { params: filters });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: filters?.page || 0,
      size: filters?.size || 20,
    };
  },

  // Get unacknowledged breaches
  getUnacknowledged: async (): Promise<SLABreach[]> => {
    const response = await api.get('/admin/support/sla-breaches/unacknowledged');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get breaches by ticket ID
  getByTicketId: async (ticketId: number): Promise<SLABreach[]> => {
    const response = await api.get(`/admin/support/sla-breaches/ticket/${ticketId}`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // Acknowledge SLA breach
  acknowledge: async (id: number, data: AcknowledgeBreachRequest): Promise<SLABreach> => {
    const response = await api.post(`/admin/support/sla-breaches/${id}/acknowledge`, data);
    return response.data;
  },

  // Get SLA analytics
  getAnalytics: async (startDate?: string, endDate?: string): Promise<SLABreachAnalytics> => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get('/admin/support/sla-breaches/analytics', { params });
    return response.data;
  },
};

// ========================================
// TERMS ACCEPTANCE EXPORT API
// ========================================

export const termsApi = {
  // Export terms acceptance logs to Excel
  exportAcceptanceLogs: async (): Promise<Blob> => {
    const response = await api.get('/admin/terms/acceptance/export/excel', {
      responseType: 'blob',
    });
    return response.data;
  },
};
